import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Logo from './Logo';
import './header.css';

// Two entries deliberately point at the same section — they read as the two
// halves of the offer (consulting, software) that section covers.
const NAV_LINKS = [
  { label: '/ai-consulting', target: 'services' },
  { label: '/software', target: 'services' },
  { label: '/process', target: 'process' },
  { label: '/faq', target: 'faq' },
  { label: '/blog', href: '/blog' },
  { label: '/contact', target: 'contact' },
];

// Used when Header is rendered outside AppContent (e.g. a standalone page).
const fallbackNavClick = (e, targetId) => {
  e.preventDefault();
  window.location.href = `/#${targetId}`;
};

const Header = ({ handleNavClick = fallbackNavClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOverHero, setIsOverHero] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  // Read inside the resize observer, which is set up once.
  const menuOpenRef = useRef(false);
  menuOpenRef.current = isMenuOpen;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The nav is fixed and overlays the hero, so publish its measured height
  // for the layout to offset against rather than hard-coding it.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const publishHeight = () => {
      // The open mobile panel makes the nav taller; publishing that would
      // shunt the whole page down while the menu is up.
      if (menuOpenRef.current) return;
      document.documentElement.style.setProperty('--ak-nav-h', `${el.offsetHeight}px`);
    };
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Only the home page has a dark hero for the nav to sit over.
  useEffect(() => {
    if (location.pathname !== '/') {
      setIsOverHero(false);
      return undefined;
    }
    const update = () => {
      const hero = document.getElementById('top');
      const navHeight = navRef.current ? navRef.current.offsetHeight : 0;
      setIsOverHero(!!hero && window.scrollY < hero.offsetHeight - navHeight);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [location.pathname]);

  // An open menu panel needs a solid ground to sit on either way.
  const inverted = isOverHero && !isMenuOpen;

  const goToSection = (e, targetId) => {
    handleNavClick(e, targetId);
    setIsMenuOpen(false);
  };

  return (
    <nav className={`ak-nav ${inverted ? 'is-over-hero' : ''}`.trim()} ref={navRef}>
      <a
        href="/#top"
        className="ak-nav-brand"
        onClick={(e) => goToSection(e, 'top')}
        aria-label="Akeyreu — home"
      >
        <Logo on={inverted ? 'dark' : 'light'} />
      </a>

      <button
        type="button"
        className={`ak-nav-toggle ${isMenuOpen ? 'is-open' : ''}`}
        onClick={() => setIsMenuOpen((open) => !open)}
        aria-label="Toggle navigation"
        aria-expanded={isMenuOpen}
      >
        <span className="ak-nav-bar" />
        <span className="ak-nav-bar" />
        <span className="ak-nav-bar" />
      </button>

      <div className={`ak-nav-links ${isMenuOpen ? 'is-open' : ''}`}>
        {NAV_LINKS.map((link) =>
          link.href ? (
            <a
              key={link.label}
              href={link.href}
              className="ak-nav-link"
              onClick={() => setIsMenuOpen(false)}
            >
              {link.label}
            </a>
          ) : (
            <a
              key={link.label}
              href={`/#${link.target}`}
              className="ak-nav-link"
              onClick={(e) => goToSection(e, link.target)}
            >
              {link.label}
            </a>
          )
        )}
      </div>

      <a
        href="/#contact"
        className="ak-nav-cta"
        onClick={(e) => goToSection(e, 'contact')}
      >
        → book_assessment
      </a>
    </nav>
  );
};

export default Header;
